// src/belediye/pages/halkMasasi/HalkMasasiLookupPage.js
//
// Halk Masası — Vatandaş Sorgu (Detay + Profil tasarım dili ile UYUMLU)
// - Pagebar: hm-ad-pagebar (white, static) + sağda Yenile
// - Layout: 50/50 (desktop), tek kolon (mobile)
// - Kart dili: md-card + detail sayfasındaki "gri head" hissi (hm-apps-head benzeri)
// - LookupPanel: kendi head'ini gizlemek için wrapper sınıfı verildi (CSS)
// - Recent: aynı kart dili + boş head kaldırıldı
//
// Route: /yonetim/halk-masasi/sorgu

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  MagnifyingGlassIcon,
  InformationCircleIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";

import {
  fetchRecentLookups,
  lookupCitizenByPhoneThunk,
  clearLookupState,
  selectLookupLoading,
  selectLookupError,
  selectLookupFound,
  selectActiveCitizen,
  selectRecentLoading,
  selectRecentError,
} from "../../redux/slices/belediyeHalkMasasiSlice";

import LookupPanel from "../../components/halkMasasi/LookupPanel";
import RecentLookups from "../../components/halkMasasi/RecentLookups";

import "./HalkMasasiLookupPage.css";

const BASE = "/yonetim/halk-masasi";

const DEBUG = String(process.env.REACT_APP_DEBUG_HALK_MASASI || "").trim() === "1";
const log = (...args) => DEBUG && console.log("[HalkMasasiLookupPage]", ...args);
const logError = (...args) => DEBUG && console.error("[HalkMasasiLookupPage]", ...args);

export default function HalkMasasiLookupPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const lookupLoading = useSelector(selectLookupLoading);
  const lookupError = useSelector(selectLookupError);
  const lookupFound = useSelector(selectLookupFound);
  const activeCitizen = useSelector(selectActiveCitizen);

  const recentLoading = useSelector(selectRecentLoading);
  const recentError = useSelector(selectRecentError);

  const [localError, setLocalError] = useState("");

  const title = useMemo(() => "Vatandaş Sorgu", []);
  const subtitle = useMemo(
    () => "Telefon numarası ile vatandaş kaydını bul ve başvurularını görüntüle.",
    []
  );

  useEffect(() => {
    return () => log("unmount");
  }, []);

  useEffect(() => {
    dispatch(clearLookupState());
    dispatch(fetchRecentLookups({ limit: 10 }));
  }, [dispatch]);

  useEffect(() => {
    if (lookupFound !== true) return;

    const cid = activeCitizen?.id;
    if (!cid) {
      logError("lookupFound=true but activeCitizen.id missing", { activeCitizen });
      return;
    }

    navigate(`${BASE}/vatandas/${cid}`, { replace: false });
  }, [lookupFound, activeCitizen, navigate]);

  const handleSubmitPhone = useCallback(
    async (phoneRaw) => {
      setLocalError("");

      const phone = String(phoneRaw || "").trim();
      if (!phone) {
        setLocalError("Telefon numarası zorunludur.");
        return;
      }

      const res = await dispatch(lookupCitizenByPhoneThunk({ phone }));

      if (lookupCitizenByPhoneThunk.rejected.match(res)) {
        logError("lookupCitizenByPhoneThunk rejected", res?.payload || res?.error);
        return;
      }

      const payload = res.payload || {};
      if (payload?.found === false) {
        setLocalError("Bu telefon numarası ile kayıt bulunamadı.");
      }
    },
    [dispatch]
  );

  const effectiveError = localError || lookupError || "";

  return (
    <div className="hm-page hm-lookup-page">
      <div className="hm-shell hm-shell--full hm-appdetail-shell">
        {/* ===== Pagebar (Detay/Profile ile aynı) — Yenile kaldırıldı ===== */}
        <div className="hm-ad-pagebar hm-ad-pagebar--static">
          <div className="hm-ad-pagebar-left">
            <div className="hm-ad-pagebar-title">{title}</div>
            <div className="hm-ad-pagebar-sub">{subtitle}</div>
          </div>
        </div>

        {/* Error */}
        {effectiveError ? (
          <div className="hm-ad-alert hm-ad-alert--error" role="alert">
            <div className="hm-ad-alert-row">
              <InformationCircleIcon width={18} height={18} />
              <div className="hm-ad-alert-body">
                <div className="hm-ad-alert-title">İşlem tamamlanamadı</div>
                <div className="hm-ad-alert-text">{String(effectiveError)}</div>
              </div>
            </div>
          </div>
        ) : null}

        {/* ===== 50/50 Grid ===== */}
        <div className="hm-lookup-grid hm-lookup-grid--5050">
          {/* LEFT: Query (same card language) */}
          <section className="md-card hm-lookup-card hm-lookup-card--query" aria-label="Telefon ile sorgu">
            <div className="hm-apps-head hm-apps-head--lookup">
              <div className="hm-apps-head-left">
                <MagnifyingGlassIcon  width={18} height={18} className="hm-apps-head-ic" />
                <div className="hm-apps-head-title">Telefon ile Sorgu</div>
              </div>

              <div className="hm-apps-head-right">
                <span className="hm-apps-count">{lookupLoading ? "Yükleniyor…" : ""}</span>
              </div>
            </div>

            <div className="hm-lookup-body">
              <div className="hm-info hm-info--soft">
                <InformationCircleIcon className="hm-info-ic" aria-hidden="true" />
                <div className="hm-info-text">
                  <div>Telefon numarasını başında 0 olmadan 10 hane olarak giriniz.</div>
                </div>
              </div>

              {/* LookupPanel kendi head'ini render ediyor; wrapper ile CSS'ten gizleyeceğiz */}
              <div className="hm-lookup-panel-wrap">
                <LookupPanel loading={lookupLoading} error={""} onSubmit={handleSubmitPhone} />
              </div>

              {lookupFound === false && !effectiveError ? (
                <div className="hm-ad-empty" role="status" style={{ marginTop: 10 }}>
                  Kayıt bulunamadı.
                </div>
              ) : null}
            </div>
          </section>

          {/* RIGHT: Recent (same card language) */}
          <section className="md-card hm-lookup-card hm-lookup-card--recent" aria-label="Son sorgular">
            <div className="hm-apps-head hm-apps-head--recent">
              <div className="hm-apps-head-left">
                <DocumentTextIcon width={18} height={18} className="hm-apps-head-ic" />
                <div className="hm-apps-head-title">Son Sorgular</div>
              </div>

              <div className="hm-apps-head-right">
                <span className="hm-apps-count">{recentLoading ? "Yükleniyor…" : ""}</span>
              </div>
            </div>

            <div className="hm-lookup-body hm-lookup-body--soft">
              {recentError ? (
                <div className="hm-ad-empty" role="status" style={{ marginBottom: 10 }}>
                  Son sorgular yüklenemedi: {String(recentError)}
                </div>
              ) : null}

              <RecentLookups
                loading={recentLoading}
                limit={10}
                onSelectCitizen={(citizenId) => {
                  if (!citizenId) return;
                  navigate(`${BASE}/vatandas/${citizenId}`, { replace: false });
                }}
              />
            </div>
          </section>
        </div>

        <div className="hm-bottom-space" />
      </div>
    </div>
  );
}